<template>
    <div>
        <b-modal :title="getTitleModal" modal-class="modal-primary" hide-footer @hidden="$emit('close')"
            no-close-on-backdrop no-close-on-esc v-model="show" size="xmd">

            <div v-if="isReadOnly" class="mb-1">
                <b-badge variant="light-warning">
                    Bono en estado {{ modalData.estado }}, los ajustes ya no se pueden modificar.
                </b-badge>
            </div>

            <div class="d-flex w-full">
                <b-table-simple ref="table-retornos-detalle" small responsive table-class="" sticky-header="60vh" hover>
                    <b-thead class="w-full">
                        <b-tr>
                            <b-th>VIAJE</b-th>
                            <b-th>ORDEN</b-th>
                            <b-th>CLIENTE</b-th>
                            <b-th>RUTA</b-th>
                            <b-th class="text-right">COSTO FLETE</b-th>
                            <b-th class="text-center">% APLICADO</b-th>
                            <b-th class="text-right">MONTO</b-th>
                            <b-th class="text-center">AJUSTE</b-th>
                            <b-th class="text-center">ACCIONES</b-th>
                        </b-tr>
                    </b-thead>

                    <b-tbody v-if="items.length">
                        <b-tr v-for="item in items" :key="item.id">
                            <b-td class="text-center">{{ item.viaje || '-' }}</b-td>
                            <b-td class="text-center">{{ item.orden || '-' }}</b-td>
                            <b-td>{{ item.cliente || '-' }}</b-td>
                            <b-td>{{ item.ruta || '-' }}</b-td>
                            <b-td class="text-right">
                                <span v-if="item.tipo_calculo === 'FIJO'" class="text-muted">-</span>
                                <span v-else-if="tieneAjusteCosto(item)">
                                    <span class="text-muted"
                                        style="text-decoration: line-through">S/ {{ item.costo_flete_original | currency }}</span>
                                    <br />
                                    <span class="text-warning">S/ {{ item.costo_flete_ajustado | currency }}</span>
                                </span>
                                <span v-else>S/ {{ item.costo_flete_original | currency }}</span>
                            </b-td>
                            <b-td class="text-center">
                                <span v-if="item.tipo_calculo === 'FIJO'">MONTO FIJO</span>
                                <span v-else-if="tieneAjustePorcentaje(item)">
                                    <span class="text-muted" style="text-decoration: line-through">{{
                                        (item.porcentaje_original * 100).toFixed(2) }}%</span>
                                    <br />
                                    <span class="text-warning">{{ (item.porcentaje_ajustado * 100).toFixed(2) }}%</span>
                                </span>
                                <span v-else>{{ (item.porcentaje_original * 100).toFixed(2) }}%</span>
                            </b-td>
                            <b-td class="text-right">
                                <span v-if="esAjustado(item)" class="text-muted"
                                    style="text-decoration: line-through; display: block">S/ {{ item.monto_original | currency }}</span>
                                <span class="text-primary">S/ {{ item.monto_calculado | currency }}</span>
                            </b-td>
                            <b-td class="text-center">
                                <b-badge v-if="esAjustado(item)" variant="light-warning" v-b-tooltip.hover
                                    :title="item.motivo_ajuste">
                                    AJUSTADO
                                </b-badge>
                                <span v-else>-</span>
                            </b-td>
                            <b-td class="text-center">
                                <div class="d-flex gap-x-2 justify-content-center" v-if="!isReadOnly">
                                    <feather-icon v-if="item.tipo_calculo !== 'FIJO'" icon="Edit2Icon"
                                        class="text-primary cursor-pointer" v-b-tooltip.hover title="Ajustar"
                                        @click="abrirAjuste(item)" />
                                    <feather-icon v-if="esAjustado(item)" icon="RotateCcwIcon"
                                        class="text-danger cursor-pointer" v-b-tooltip.hover title="Revertir ajuste"
                                        @click="revertirAjuste(item)" />
                                </div>
                            </b-td>
                        </b-tr>
                    </b-tbody>

                    <b-tbody v-else>
                        <b-tr>
                            <b-td colspan="9" class="text-center">
                                <span class="text-muted">No se encontraron viajes de retorno para este bono.</span>
                            </b-td>
                        </b-tr>
                    </b-tbody>

                    <b-tfoot v-if="items.length">
                        <b-tr>
                            <b-td colspan="6" class="text-right" style="opacity: 0.7; font-size: 13px">
                                TOTAL RETORNOS:
                            </b-td>
                            <b-td colspan="3" style="opacity: 0.7; font-size: 13px">
                                <b-badge variant="light-primary">
                                    S/ {{ totalRetornos | currency }}
                                </b-badge>
                            </b-td>
                        </b-tr>
                    </b-tfoot>
                </b-table-simple>
            </div>

            <div class="d-flex justify-content-end gap-x-3 mt-1">
                <b-button variant="outline-primary" @click="$emit('close')">
                    CERRAR
                </b-button>
            </div>
        </b-modal>

        <AjusteRetornoModal v-if="showAjusteModal" :modalData="ajusteData" @close="showAjusteModal = false"
            @refresh="handleAjusteGuardado" />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import BonoService from "@/views/rrhh/views/payroll/components/bonos/service/bono.service.js";
import AjusteRetornoModal from "@/views/rrhh/views/payroll/components/bonos/components/AjusteRetornoModal.vue";

const ESTADOS_NO_EDITABLES = ["APROBADO", "ANULADO"];

export default {
    components: {
        AjusteRetornoModal,
    },
    props: {
        modalData: {
            type: Object,
            require: true,
        },
    },
    data() {
        return {
            show: true,
            items: [],
            showAjusteModal: false,
            ajusteData: null,
        };
    },
    async mounted() {
        await this.getRetornosDetalle();
    },
    computed: {
        ...mapGetters({
            currentUser: "auth/currentUser",
        }),
        getTitleModal() {
            return "DETALLE BONO DE RETORNOS: " + (this.modalData && this.modalData.nombre_completo ? this.modalData.nombre_completo : "");
        },
        isReadOnly() {
            return ESTADOS_NO_EDITABLES.includes(this.modalData.estado);
        },
        totalRetornos() {
            return this.items.reduce((acc, item) => acc + Number(item.monto_calculado || 0), 0);
        },
    },
    methods: {
        tieneAjusteCosto(item) {
            return item.costo_flete_ajustado !== null && item.costo_flete_ajustado !== undefined;
        },
        tieneAjustePorcentaje(item) {
            return item.porcentaje_ajustado !== null && item.porcentaje_ajustado !== undefined;
        },
        esAjustado(item) {
            return this.tieneAjusteCosto(item) || this.tieneAjustePorcentaje(item);
        },
        async getRetornosDetalle() {
            try {
                const { data } = await BonoService.getRetornosDetalle({ bono_id: this.modalData.id });
                this.items = data.data || [];
            } catch (error) {
                this.items = [];
                console.log(error);
            }
        },
        abrirAjuste(item) {
            this.ajusteData = item;
            this.showAjusteModal = true;
        },
        async revertirAjuste(item) {
            try {
                const confirm = await this.showConfirmSwal();
                if (!confirm.isConfirmed) return;

                this.addPreloader();
                await BonoService.quitarAjusteRetorno({ id: item.id });
                this.showToastSuccess("Ajuste revertido correctamente");
                await this.getRetornosDetalle();
                this.$emit("refresh");
            } catch (error) {
                this.showToastError(error?.response?.data?.message);
            } finally {
                this.removePreloader();
            }
        },
        async handleAjusteGuardado() {
            this.showAjusteModal = false;
            await this.getRetornosDetalle();
            this.$emit("refresh");
        },
    },
};
</script>
